import { useDebounceFn } from '@vueuse/core'
import { useNuxtApp, useRoute } from 'nuxt/app'
import { watch } from 'vue'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { getConfig } from '@common/core-layer/config'
import { log } from '@common/core-layer/services/logger.service'
import { isCoursesContextPath } from '@common/core-layer/utils/route-path-segments'
import { preLoginRegionHeader, readRegionFromToken } from '@common/http-layer/utils/regional-host'

import type { Pinia } from 'pinia'

export default defineNuxtPlugin(async () => {
  const route = useRoute()
  const pinia = useNuxtApp().$pinia as Pinia
  const { store: authStore } = useAuth(pinia)

  let currentSource: 'courses' | 'communities' = 'communities'

  const resolvedRegion = (token?: string | null): 'us' | 'au' =>
    readRegionFromToken(token) ?? (preLoginRegionHeader() === 'au' ? 'au' : 'us')

  /**
   * Determine DRM source based on route path
   * - Top-level `/courses` app routes and group `/learning/courses/` paths → courses
   * - Otherwise → communities (including group slugs like `courses-MD`)
   */
  const getDrmSource = (path: string): 'courses' | 'communities' => {
    if (isCoursesContextPath(path)) {
      return 'courses'
    }
    return 'communities'
  }

  const drmHeadersForRegion = (region: 'us' | 'au') => ({
    source: 'PORTAL_USER',
    // SDK resolveRegion also reads custom headers when the pin is unset.
    ...(region === 'au' ? { 'x-dr-region': 'au' as const } : {}),
  })

  /**
   * Pin SDK region from the latest auth/host signal.
   * Call after init and on every token change — init can race ahead of token hydration.
   */
  const syncDrmRegion = async (token?: string | null) => {
    try {
      const { setRegion } = await import('@ghl-revex/assets-drm-client/setup')
      setRegion(resolvedRegion(token))
    } catch (error) {
      log.error('Failed to update assets drm region', { error, source: currentSource })
    }
  }

  /**
   * Initialize or reinitialize Assets DRM client with the given source
   */
  const initializeDrmClient = async (source: 'courses' | 'communities') => {
    const config = getConfig()
    try {
      const { initAssetsDrmClient, setRegion } = await import('@ghl-revex/assets-drm-client/setup')

      // Resolve region after the dynamic import so a token that hydrated during
      // the await is not missed (common on AU clientclub cold load).
      const region = resolvedRegion(authStore.token)

      initAssetsDrmClient({
        // in local i.e development, point assets drm to staging else use config mode
        env: config?.mode === 'development' ? 'staging' : config?.mode || 'staging',
        source,
        region,
        getAuthTokenCallback: getAuthTokenCallback(),
        headers: drmHeadersForRegion(region),
      })
      // Re-pin: token may have landed between resolvedRegion() and init return,
      // and init({ region: 'us' }) would otherwise overwrite a concurrent setRegion('au').
      setRegion(resolvedRegion(authStore.token))
      currentSource = source
    } catch (error) {
      log.error('Failed to initialize assets drm client', { error, source })
    }
  }

  // Initialize with current route source
  const initialSource = getDrmSource(route.path)
  await initializeDrmClient(initialSource)

  // Debounced route watcher to handle source changes
  const handleRouteChange = useDebounceFn(async (newPath: string) => {
    const newSource = getDrmSource(newPath)
    if (newSource !== currentSource) {
      await initializeDrmClient(newSource)
    }
  }, 300) // 300ms debounce

  // Watch for route changes
  watch(() => route.path, handleRouteChange)

  // immediate: token is often already on the store after async init; without this,
  // AU region from JWT never re-pins the SDK and assets-license stays on US host.
  watch(
    () => authStore.token,
    token => {
      void syncDrmRegion(token)
    },
    { immediate: true }
  )
})

function getAuthTokenCallback() {
  return async () => {
    const pinia = useNuxtApp().$pinia as Pinia
    const { store } = useAuth(pinia)
    return store.token || ''
  }
}
